/**
 * Centralized Logging Utility
 * Provides structured logging with correlation IDs, context tracking, and configurable levels
 * Designed for debugging sales OCR workflow and other features
 */

// ============================================================================
// Types & Constants
// ============================================================================

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';

export interface LogContext {
  userId?: string;
  sessionId?: string;
  correlationId?: string;
  component?: string;
  operation?: string;
  [key: string]: unknown;
}

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: LogContext;
  error?: Error;
  data?: Record<string, unknown>;
}

// ============================================================================
// Configuration
// ============================================================================

const LOG_CONFIG = {
  // Enable logging in development by default
  enabled:
    process.env.NODE_ENV === 'development' ||
    process.env.REACT_APP_ENABLE_LOGGING === 'true',

  // Log level hierarchy: DEBUG < INFO < WARN < ERROR
  minLevel: (process.env.REACT_APP_LOG_LEVEL as LogLevel) || 'DEBUG',

  // Maximum number of log entries to keep in memory
  maxEntries: parseInt(process.env.REACT_APP_MAX_LOG_ENTRIES || '1000', 10),

  // Enable structured logging to console
  structuredOutput: process.env.REACT_APP_STRUCTURED_LOGS === 'true',
};

// ============================================================================
// Logger Class
// ============================================================================

class Logger {
  private entries: LogEntry[] = [];
  private correlationIdCounter = 0;
  private defaultContext: LogContext = {};

  constructor() {
    // Generate a session ID for this browser session
    this.defaultContext.sessionId = this.generateSessionId();
  }

  /**
   * Generate a unique correlation ID for tracking related operations
   */
  generateCorrelationId(): string {
    this.correlationIdCounter++;
    const timestamp = Date.now().toString(36);
    return `corr_${timestamp}_${this.correlationIdCounter}`;
  }

  /**
   * Generate a session ID for this browser session
   */
  private generateSessionId(): string {
    return `sess_${Date.now().toString(36)}_${Math.random().toString(36).substring(2)}`;
  }

  /**
   * Set default context that will be included in all log entries
   */
  setDefaultContext(context: Partial<LogContext>): void {
    this.defaultContext = { ...this.defaultContext, ...context };
  }

  /**
   * Check if logging is enabled and level meets minimum threshold
   */
  private shouldCapture(level: LogLevel): boolean {
    const levelPriority = { DEBUG: 0, INFO: 1, WARN: 2, ERROR: 3 };
    return levelPriority[level] >= levelPriority[LOG_CONFIG.minLevel];
  }

  /**
   * Create a structured log entry
   */
  private createEntry(
    level: LogLevel,
    message: string,
    context?: LogContext,
    error?: Error,
    data?: Record<string, unknown>,
  ): LogEntry {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      context: { ...this.defaultContext, ...context },
      ...(error && { error }),
      ...(data && { data }),
    };

    // Store entry in memory (with size limit)
    if (this.entries.length >= LOG_CONFIG.maxEntries) {
      this.entries.shift(); // Remove oldest entry
    }
    this.entries.push(entry);

    return entry;
  }

  /**
   * Output log entry to console with appropriate formatting
   */
  private output(entry: LogEntry): void {
    const { timestamp, level, message, context, error, data } = entry;

    if (!LOG_CONFIG.enabled) {
      this.forward(entry);
      return;
    }

    if (LOG_CONFIG.structuredOutput) {
      // Structured JSON output for log aggregation
      console.log(JSON.stringify(entry));
    } else {
      // Human-readable console output for development
      const contextStr = context
        ? ` [${Object.entries(context)
            .filter(([_, value]) => value !== undefined)
            .map(([key, value]) => `${key}:${value}`)
            .join(', ')}]`
        : '';

      const prefix = `[${timestamp}] ${level}${contextStr}`;

      switch (level) {
        case 'DEBUG':
          console.debug(`🔍 ${prefix} ${message}`, data || '');
          break;
        case 'INFO':
          console.info(`ℹ️ ${prefix} ${message}`, data || '');
          break;
        case 'WARN':
          console.warn(`⚠️ ${prefix} ${message}`, data || '');
          break;
        case 'ERROR':
          console.error(`❌ ${prefix} ${message}`, error || data || '');
          if (error?.stack) {
            console.error('Stack trace:', error.stack);
          }
          break;
      }
    }
  }

  private forward(entry: LogEntry): void {
    // Future transport (Sentry/DataDog) integration point
    void entry;
  }

  /**
   * Log debug message
   */
  debug(
    message: string,
    context?: LogContext,
    data?: Record<string, unknown>,
  ): void {
    if (!this.shouldCapture('DEBUG')) return;
    const entry = this.createEntry('DEBUG', message, context, undefined, data);
    this.output(entry);
  }

  /**
   * Log info message
   */
  info(
    message: string,
    context?: LogContext,
    data?: Record<string, unknown>,
  ): void {
    if (!this.shouldCapture('INFO')) return;
    const entry = this.createEntry('INFO', message, context, undefined, data);
    this.output(entry);
  }

  /**
   * Log warning message
   */
  warn(
    message: string,
    context?: LogContext,
    data?: Record<string, unknown>,
  ): void {
    if (!this.shouldCapture('WARN')) return;
    const entry = this.createEntry('WARN', message, context, undefined, data);
    this.output(entry);
  }

  /**
   * Log error message
   */
  error(
    message: string,
    error?: Error,
    context?: LogContext,
    data?: Record<string, unknown>,
  ): void {
    if (!this.shouldCapture('ERROR')) return;
    const entry = this.createEntry('ERROR', message, context, error, data);
    this.output(entry);
  }

  /**
   * Get all log entries (useful for debugging or sending to support)
   */
  getEntries(): LogEntry[] {
    return [...this.entries];
  }

  /**
   * Clear all log entries
   */
  clearEntries(): void {
    this.entries = [];
  }

  /**
   * Create a scoped logger with predefined context
   */
  scope(scopeContext: LogContext): ScopedLogger {
    return new ScopedLogger(this, scopeContext);
  }
}

// ============================================================================
// Scoped Logger for Component/Feature Specific Logging
// ============================================================================

class ScopedLogger {
  constructor(
    private parent: Logger,
    private scopeContext: LogContext,
  ) {}

  debug(
    message: string,
    context?: LogContext,
    data?: Record<string, unknown>,
  ): void {
    this.parent.debug(message, { ...this.scopeContext, ...context }, data);
  }

  info(
    message: string,
    context?: LogContext,
    data?: Record<string, unknown>,
  ): void {
    this.parent.info(message, { ...this.scopeContext, ...context }, data);
  }

  warn(
    message: string,
    context?: LogContext,
    data?: Record<string, unknown>,
  ): void {
    this.parent.warn(message, { ...this.scopeContext, ...context }, data);
  }

  error(
    message: string,
    error?: Error,
    context?: LogContext,
    data?: Record<string, unknown>,
  ): void {
    this.parent.error(
      message,
      error,
      { ...this.scopeContext, ...context },
      data,
    );
  }

  generateCorrelationId(): string {
    return this.parent.generateCorrelationId();
  }
}

// ============================================================================
// Exports
// ============================================================================

// Global logger instance
export const logger = new Logger();

// Convenience functions for common logging patterns
export const createSalesLogger = (correlationId?: string) =>
  logger.scope({
    component: 'SalesOCR',
    correlationId: correlationId || logger.generateCorrelationId(),
  });

export const createFormLogger = (formName: string, correlationId?: string) =>
  logger.scope({
    component: 'FormValidation',
    operation: formName,
    correlationId: correlationId || logger.generateCorrelationId(),
  });

export const createApiLogger = (endpoint: string, correlationId?: string) =>
  logger.scope({
    component: 'ApiClient',
    operation: endpoint,
    correlationId: correlationId || logger.generateCorrelationId(),
  });

export const createNavigationLogger = (correlationId?: string) =>
  logger.scope({
    component: 'Navigation',
    correlationId: correlationId || logger.generateCorrelationId(),
  });

export const createRouteLogger = (routeName: string, correlationId?: string) =>
  logger.scope({
    component: 'Route',
    operation: routeName,
    correlationId: correlationId || logger.generateCorrelationId(),
  });

// Type exports
export type ScopedLoggerType = ScopedLogger;
